import { AvailableLanguages, Country } from "./types";

export const capitalizeFirstLetter = (
  str: string | Country["spanish-name"] | Country["english-name"]
) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const removeSpecialCharts = (str: string) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z ]/g, "")
    .toLocaleLowerCase();
};

export const parsedCountryName = (
  country: Country["spanish-name"] | Country["english-name"] | string
) => {
  return removeSpecialCharts(country);
};

export const countryMatchesInput = (
  country: Country,
  focusedCountry: string
) => {
  return (
    (parsedCountryName(country["english-name"]).includes(focusedCountry) &&
      focusedCountry) ||
    (parsedCountryName(country["spanish-name"]).includes(focusedCountry) &&
      focusedCountry)
  );
};

const normalizeForSearch = (value: string) =>
  parsedCountryName(value).replace(/\s+/g, " ").trim();

const words = (value: string) =>
  normalizeForSearch(value)
    .split(" ")
    .map((w) => w.trim())
    .filter((w) => w.length > 0);

type MatchedField = "english" | "spanish";

const scoreQueryAgainstName = (query: string, name: string) => {
  const q = normalizeForSearch(query);
  const n = normalizeForSearch(name);

  if (q.length === 0) return -1;
  if (n.length === 0) return -1;
  if (n === q) return 1000;

  const qWords = words(q);
  const nWords = words(n);

  // If the user typed a very short single token (e.g. "an"), avoid random matches.
  if (qWords.length === 1 && qWords[0].length < 3) return -1;

  const includesQuery = n.includes(q);
  const startsWithQuery = n.startsWith(q);
  const allQueryWordsPresent = qWords.every((qw) => nWords.includes(qw));
  const allQueryWordsAsPrefixes = qWords.every((qw) =>
    nWords.some((nw) => nw.startsWith(qw))
  );

  // At least one of those has to hold for this to be a match at all. The
  // length term below is only a tiebreaker, but it used to be scored
  // unconditionally — so a query matching nothing still collected up to 70
  // points from any name of a similar length, and the search always returned
  // *something*. That's how "antartida" (9 letters, and the dataset has no
  // Antarctic country) came back as "Australia" (9 letters), and "zzzzzzzz"
  // as "Bélgica".
  if (
    !includesQuery &&
    !startsWithQuery &&
    !allQueryWordsPresent &&
    !allQueryWordsAsPrefixes
  ) {
    return -1;
  }

  let score = 0;

  if (includesQuery) score += 220;
  if (startsWithQuery) score += 120;
  if (allQueryWordsPresent) score += 160;
  if (allQueryWordsAsPrefixes) score += 80;

  // Prefer tighter matches (shorter distance between query and full name).
  const lenDelta = Math.max(0, n.length - q.length);
  score += Math.max(0, 70 - lenDelta);

  return score;
};

export const CONTINENTS = [
  "Africa",
  "North America",
  "South America",
  "Asia",
  "Europe",
  "Oceania",
  "Antarctica",
] as const;

export type Continent = (typeof CONTINENTS)[number];

/**
 * The continents worth offering as a filter. Antarctica is left out: the
 * dataset has no Antarctic country, its sole member is the French Southern
 * Territories (self-described as "subantarctic" — the shape the map draws is
 * Kerguelen and Crozet, in the Indian Ocean), and the artwork has no Antarctic
 * landmass at all. Picking it could only promise a continent and deliver an
 * island. It stays in CONTINENTS, where the quiz still uses it as a plausible
 * wrong answer.
 */
export const FILTERABLE_CONTINENTS = CONTINENTS.filter(
  (continent) => continent !== "Antarctica"
);

// Source data ("EnglishData.json"/"SpanishData.json") stores free-text continent
// descriptions (e.g. "Asia (Middle East)", "North America (Caribbean)"). Order
// matters: more specific patterns must be checked before broader ones, and
// transcontinental countries are bucketed under the continent listed first.
const CONTINENT_PATTERNS: Array<[Continent, RegExp]> = [
  ["Antarctica", /antarctica/i],
  ["Oceania", /oceania/i],
  ["Africa", /africa/i],
  ["North America", /central america|north america/i],
  ["South America", /south america/i],
  ["Asia", /asia/i],
  ["Europe", /europe/i],
];

// A handful of entries only say "America" with no further detail. Resolve
// those by real-world geography rather than guessing from the raw string.
const AMBIGUOUS_AMERICA_OVERRIDES: Record<string, Continent> = {
  Haiti: "North America",
  Mexico: "North America",
  Panama: "North America",
  Peru: "South America",
  "French Guiana (overseas region of France)": "South America",
  "Falkland Islands": "South America",
  Belize: "North America",
  "Dominican Republic": "North America",
  Nicaragua: "North America",
  "Costa Rica": "North America",
  "United States": "North America",
  "Puerto Rico": "North America",
};

export const normalizeContinent = (raw?: string): Continent | undefined => {
  if (!raw) return undefined;
  const match = CONTINENT_PATTERNS.find(([, pattern]) => pattern.test(raw));
  return match?.[0];
};

export const resolveContinent = (
  englishName: string,
  rawContinent?: string
): Continent | undefined =>
  AMBIGUOUS_AMERICA_OVERRIDES[englishName] ?? normalizeContinent(rawContinent);

export const CONTINENT_LABELS: Record<
  AvailableLanguages,
  Record<Continent, string>
> = {
  [AvailableLanguages.EN]: {
    Africa: "Africa",
    "North America": "North America",
    "South America": "South America",
    Asia: "Asia",
    Europe: "Europe",
    Oceania: "Oceania",
    Antarctica: "Antarctica",
  },
  [AvailableLanguages.ES]: {
    Africa: "África",
    "North America": "América del Norte",
    "South America": "América del Sur",
    Asia: "Asia",
    Europe: "Europa",
    Oceania: "Oceanía",
    Antarctica: "Antártida",
  },
};

export const bestCountryMatch = (
  countries: Country[],
  rawInput: string
):
  | {
      country: Country;
      matchedField: MatchedField;
    }
  | undefined => {
  const query = normalizeForSearch(rawInput);
  if (!query) return undefined;

  let best:
    | { country: Country; matchedField: MatchedField; score: number }
    | undefined;

  for (const country of countries) {
    const en = country["english-name"] ?? "";
    const es = country["spanish-name"] ?? "";

    const enScore = scoreQueryAgainstName(query, en);
    const esScore = scoreQueryAgainstName(query, es);

    const matchedField: MatchedField = enScore >= esScore ? "english" : "spanish";
    const score = Math.max(enScore, esScore);

    if (score < 0) continue;
    if (!best || score > best.score) {
      best = { country, matchedField, score };
    }
  }

  return best ? { country: best.country, matchedField: best.matchedField } : undefined;
};

// --- language switching -----------------------------------------------------

/**
 * Each language's name *in that language*. A switcher labelled in the language
 * you're leaving is useless to someone who can't read it: an English speaker
 * who lands on the Spanish version needs to spot "English", not "Cambiar
 * idioma". Deliberately not flags — flags are countries, not languages, and
 * Spanish belongs to twenty-odd of them.
 */
export const LANGUAGE_ENDONYMS: Record<AvailableLanguages, string> = {
  [AvailableLanguages.EN]: "English",
  [AvailableLanguages.ES]: "Español",
};

/** With only two languages, switching is a toggle rather than a choice. */
export const otherLanguage = (language: AvailableLanguages) =>
  language === AvailableLanguages.ES
    ? AvailableLanguages.EN
    : AvailableLanguages.ES;

export const switchLanguageLabel = (current: AvailableLanguages) =>
  LANGUAGE_ENDONYMS[otherLanguage(current)];

/** Spells out the action for screen readers, in the language being switched to. */
export const switchLanguageAria = (current: AvailableLanguages) =>
  current === AvailableLanguages.ES
    ? `Switch to English`
    : `Cambiar a Español`;
